/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";

import { Tab } from "../../../../ui/Tab";
import { isPortalDomain } from "../../../../utility/hosting";
import { BrandInitialEventDisplay } from "../../components/BrandInitialEventDisplay";
import { CentrallyLocatedLoader } from "../../components/CentrallyLocatedLoader";
import { EventSearchResultList } from "../../components/EventSearchResultList";
import { GoodsSearchResultList } from "../../components/GoodsSearchResultList";
import { NoSearchResult } from "../../components/NoSearchResult";
import { PortalInitialEventDisplay } from "../../components/PortalInitialEventDisplay";
import { getSelectedTabIndex } from "./functions/getSelectedTabIndex";

import type { Size } from "../../../../ui/Tab/types";
import type { EventSearchResultItem } from "../../types/eventSearchResultItem";
import type { GoodsSearchResultItem } from "../../types/goodsSearchResultItem";
import type { useI18n } from "hooks/i18n/i18n";
import type { ComponentProps } from "react";

type TabContents = ComponentProps<typeof Tab>["contents"];

type Input = {
  eventResultCount: number;
  eventSearchResults: EventSearchResultItem[];
  goodsResultCount: number;
  goodsSearchResults: GoodsSearchResultItem[];
  handleLoadMoreEvents: () => void;
  handleLoadMoreGoodsList: () => void;
  hasSearched: boolean;
  isLoading: boolean;
  isMoreLoading: boolean;
  onSelectTab: (selectedTabIndex: number) => void;
  search: string;
  t: ReturnType<typeof useI18n>["t"];
  tabSize: Size;
};
type Output = () => JSX.Element;
type CreateGetContents = (input: Input) => Output;

export const createGetContents: CreateGetContents = (input) => {
  const {
    eventResultCount,
    eventSearchResults,
    goodsResultCount,
    goodsSearchResults,
    handleLoadMoreEvents,
    handleLoadMoreGoodsList,
    hasSearched,
    isLoading,
    isMoreLoading,
    onSelectTab,
    search,
    t,
    tabSize,
  } = input;

  const getContents: Output = () => {
    if (isLoading) return <CentrallyLocatedLoader />;

    if (!hasSearched) {
      if (isPortalDomain()) return <PortalInitialEventDisplay />;
      return <BrandInitialEventDisplay />;
    }

    const hasEventSearchResult = eventResultCount > 0;
    const hasGoodsSearchResult = goodsResultCount > 0;

    if (!hasEventSearchResult && !hasGoodsSearchResult)
      return <NoSearchResult />;

    const contents: TabContents = [];
    if (hasEventSearchResult) {
      contents.push({
        contentElement: (
          <EventSearchResultList
            events={eventSearchResults}
            isMoreLoading={isMoreLoading}
            totalCount={eventResultCount}
            onClickShowMore={handleLoadMoreEvents}
          />
        ),
        title: t("search.tab.event"),
      });
    }
    if (hasGoodsSearchResult) {
      contents.push({
        contentElement: (
          <GoodsSearchResultList
            goodsList={goodsSearchResults}
            isMoreLoading={isMoreLoading}
            totalCount={goodsResultCount}
            onClickShowMore={handleLoadMoreGoodsList}
          />
        ),
        title: t("search.tab.goods"),
      });
    }
    const selectedTabIndex = getSelectedTabIndex({
      contentsCount: contents.length,
      search,
    });
    return (
      <Tab
        contents={contents}
        defaultSelectedIndex={selectedTabIndex}
        onSelectTab={onSelectTab}
        size={tabSize}
      />
    );
  };

  return getContents;
};
